import partition from 'lodash/partition';

import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {
  Action,
  canPerformPropertyAggregation,
  EntryPart,
  EntrySectionUpdateEvent,
  FiltersOperator,
  isGroupedEntry,
  isPropertyEntry,
  QueryBehaviorEntry,
  QuerySuperEntry,
} from 'common/widgets/query-entry-section/types';
import {ChangeEvent} from 'common/components/select/types';
import {SectionItem} from 'common/components/items-menu/types';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {generateFilterTrackingProps} from 'common/widgets/query-block/filter-menu/util';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';

import {BaseEntryRow} from '../base';
import type {EntryRow} from '../base/types';
import {applyFilterOperatorToFilter, getFilterOperatorsSections} from '../filter/utils';
import {SelectedOperator} from '../filter/constants';
import {SuperEntryRowEl} from './template';

/**
 * QuerySuperEntryI is a catch-all row renderer for every selector type
 *
 * TODO(pablo): break up all functionality from QueryEntrySection into their respective
 * selector type classes (SegmentEntry, CohortEntry, etc..)
 */
export class SuperEntryRow extends BaseEntryRow<QuerySuperEntry> implements EntryRow<QuerySuperEntry> {
  template = SuperEntryRowEl;

  isPlaceholder() {
    if (
      !this.hasEventValue(this.entry) ||
      !this.entry.filters.every((filter) => new PropertyFilter(filter).isValid())
    ) {
      return true;
    }

    if (canPerformPropertyAggregation(this.entry.type)) {
      return !this.hasPropertyValue();
    }

    /** @todo [pablo] move all of behavior specific logic to a behavior entry type definition */
    if ((this.entry as QueryBehaviorEntry).entryFilter) {
      const entryFilter = new PropertyFilter((this.entry as QueryBehaviorEntry).entryFilter);
      return !entryFilter.isValid();
    }

    return false;
  }

  hasPropertyValue() {
    const {property} = this.entry;
    return Boolean(property && (property.value || property.customPropertyId));
  }

  hasEventValue(entry: QuerySuperEntry = this.entry) {
    // When adding a new row for filter/segment, the entry will be initialized to null. For other entry types,
    // a placeholder entry is used with `value` field null.
    if (isPropertyEntry(entry)) {
      return !!this.entry.event;
    } else if (isGroupedEntry(entry)) {
      // Grouped entries are managed by a sub- query-entry-section and always have an event.
      return true;
    } else {
      return !!entry.event?.value;
    }
  }

  get entryFilter() {
    const entryFilter = (this.entry as QueryBehaviorEntry).entryFilter;
    return entryFilter && new PropertyFilter(entryFilter);
  }

  /**
   * Funnel behaviors (did A then B) are defined with frequency filters using
   * the equals or at least numeric operators
   */
  getEntryFilterOperators() {
    const isFunnelBehavior = this.entry.behavioralFiltersOperator === FiltersOperator.Then;
    const filterOperatorsOverrides = isFunnelBehavior ? [NumberOperator.AtLeast, NumberOperator.EqualTo] : null;

    return getFilterOperatorsSections(this.entryFilter, filterOperatorsOverrides);
  }

  handleEntryFilterOperatorUpdate(ev: ChangeEvent<SectionItem<SelectedOperator>>) {
    const [filterOperator] = ev.detail.item.value;
    const filter = applyFilterOperatorToFilter(
      filterOperator,
      new PropertyFilter((this.entry as QueryBehaviorEntry).entryFilter),
    );

    this.updateEntryFilter(filter);
  }

  handleEntryFilterValueUpdate(attribute: keyof PropertyFilter, value) {
    this.updateEntryFilter(
      new PropertyFilter((this.entry as QueryBehaviorEntry).entryFilter).setAttribute(attribute, value),
    );
  }

  updateEntryFilter(filter: PropertyFilter, trackingProps = null) {
    // focus on filter value when operator changes filter to an invalid state
    !filter.isValid() && this.section.setActiveEntry(this.index, EntryPart.EntryFilterValue);

    const entry = {
      ...this.entry,
      entryFilter: filter,
    };

    this.section.updateEntries(Action.Edit, {
      entry,
      index: this.index,
      trackingProps: trackingProps ?? generateFilterTrackingProps(filter),
    });

    this.section.trackAction(this.index, entry, Action.EditFilter);
  }

  /**
   * Handles filter updates from a nested QueryEntrySection
   */
  handleFilterSectionUpdate(ev: EntrySectionUpdateEvent, source: EntryPart): void {
    const queryReason = ev.detail.queryReason;
    let filters: PropertyFilter[] = [];
    let behavioralFilters: PropertyFilter[] = [];

    [behavioralFilters, filters] = partition(this.entry.filters, isFirstTimeEventFilter);

    const {entries, updatedEntry} = ev.detail;
    // update the entry's filters operator to match the selected operator from the incoming
    // updated entry, if it's not available fetch it from the first commited filter
    const filtersOperator = updatedEntry?.filtersOperator ?? entries[0]?.filtersOperator;

    const updatedFilters = {
      [EntryPart.Filters]: filters,
      [EntryPart.BehavioralFilters]: behavioralFilters,
      [source]: entries.map((entry) => entry.event),
    };

    const entry = {
      ...this.entry,
      filters: [...updatedFilters[EntryPart.Filters], ...updatedFilters[EntryPart.BehavioralFilters]],
      filtersOperator,
    };

    this.section.updateEntries(Action.Edit, {entry, index: this.index, queryReason});
  }
}
